import * as React from "react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { ROUTES } from "@app/constants/routes";
import { useAppDispatch, useAppSelector } from "@app/redux/store";
import { userAccountThunks } from "@app/redux/thunks/user-account.thunk";

interface IRequireAdminProps {
  children: JSX.Element;
}

/**
 * Route guard: only ADMIN users may see the wrapped page. Complements the admin-only ESB guards and
 * the nav visibility — so directly opening the URL as a non-admin redirects away instead of rendering.
 */
export const RequireAdmin = ({ children }: IRequireAdminProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { role } = useAppSelector((state) => state.userAccount);
  // Tracks whether our getRole attempt has settled. Local state instead of the shared
  // `userAccount.isLoading` flag: that flag is also flipped by setUser/setUserId, and a failing
  // getRole never resets it — which would leave this guard rendering null (a blank page) forever.
  const [roleCheckSettled, setRoleCheckSettled] = useState(false);

  useEffect(() => {
    if (role) {
      return;
    }
    Promise.resolve(dispatch(userAccountThunks.getRole()))
      .finally(() => setRoleCheckSettled(true))
      // The thunk swallows its own errors; this catch only satisfies promise/catch-or-return.
      .catch(() => undefined);
  }, [role, dispatch]);

  if (!role) {
    // Not resolved yet → render nothing (avoids a premature redirect). Resolved but still no
    // role (e.g. an unparsable token) → treat as non-admin and redirect instead of going blank.
    return roleCheckSettled ? <Navigate to={ROUTES.STORE_OVERVIEW} replace /> : null;
  }

  return role === "admin" ? children : <Navigate to={ROUTES.STORE_OVERVIEW} replace />;
};
